import { computed, inject, Injectable, signal } from '@angular/core';
import {
  CreateRecruitGroupDto,
  RecruitGroupFiltersDto,
  RecruitGroupResponseDto,
  RecruitGroupsDto,
  RecruitGroupShareEmailDto,
  RecruitGroupShareLinkDto,
  RecruitGroupShareReviewDto,
  RecruitGroupInviteApprovedDto,
  RecruitGroupShareViewDto,
  RecruitGroupTypesEnum,
  AddCreatorsToGroupResultDto,
  AddCreatorByHandleDto,
} from '../../common/general-types/recruit-groups.dto';
import { catchError, firstValueFrom, forkJoin, map, Observable, of, tap, throwError } from 'rxjs';
import { CurrentUserService } from '../../common/current-user/current-user.service';
import { environment } from '../../../environments/environment';
import { FirebaseService } from '../../common/firebase/firebase.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { FilterItemDto } from '../../common/general-types/filters.dto';
import { CreatorResponseDto } from '../../common/general-types/creators.dto';
import { VideoResponseDto } from '../../common/general-types/videos.dto';

/** Shown when a creator cannot be removed because the group is already synced to Noodle. */
export const GROUP_LOCKED_MESSAGE =
  "This group is locked — creators can't be removed once it's been sent for screening or monitoring.";

@Injectable({
  providedIn: 'root'
})
export class RecruitGroupService {

  readonly myRecruitGroups = signal<RecruitGroupResponseDto[] | undefined>(undefined);
  readonly brandRecruitGroups = signal<RecruitGroupResponseDto[] | undefined>(undefined);
  readonly agencyRecruitGroups = signal<RecruitGroupResponseDto[] | undefined>(undefined);
  readonly allRecruitGroups = signal<RecruitGroupResponseDto[] | undefined>(undefined);
  readonly globalRecruitGroups = signal<RecruitGroupResponseDto[] | undefined>(undefined);
  readonly creatorGroupMembershipRevision = signal(0);
  private lastGroupsInitKey: string | undefined;

  /** Group Filter Options */
  readonly myGroupsFilters = signal<FilterItemDto[] | undefined>(undefined);
  readonly brandGroupsFilters = signal<FilterItemDto[] | undefined>(undefined);
  readonly agencyGroupsFilters = signal<FilterItemDto[] | undefined>(undefined);
  readonly allGroupsFilters = signal<FilterItemDto[] | undefined>(undefined);
  readonly globalGroupsFilters = signal<FilterItemDto[] | undefined>(undefined);

  /** Services */
  private currentUserService = inject(CurrentUserService);
  private http = inject(HttpClient);
  private firebaseService = inject(FirebaseService);
  /** Services - END */

  constructor() { }

  notifyCreatorGroupMembershipChanged(): void {
    this.creatorGroupMembershipRevision.update((count) => count + 1);
  }

  visibleRecruitGroupIds(): Set<string> {
    const merged = [
      ...(this.allRecruitGroups() ?? []),
      ...(this.myRecruitGroups() ?? []),
      ...(this.brandRecruitGroups() ?? []),
      ...(this.agencyRecruitGroups() ?? []),
    ];
    const ids = new Set<string>();
    for (const group of merged) {
      if (group.group_type === RecruitGroupTypesEnum.CREATOR_INTEREST) {
        continue;
      }
      ids.add(group.id);
    }
    return ids;
  }

  membershipCountInVisibleGroups(recruitGroupIds: readonly string[] | undefined): number {
    if (!recruitGroupIds?.length) {
      return 0;
    }
    const visible = this.visibleRecruitGroupIds();
    let count = 0;
    for (const id of recruitGroupIds) {
      if (visible.has(id)) {
        count += 1;
      }
    }
    return count;
  }

  async initRecruitmentGroups() {
    const initKey = `${this.firebaseService.userId}:${this.currentUserService.focussedBrand?.id ?? ''}`;
    if (this.lastGroupsInitKey !== initKey) {
      this.clearCachedRecruitmentGroups();
      this.lastGroupsInitKey = initKey;
    }
    this.myRecruitGroups.set(await firstValueFrom(this.getMyRecruitGroups()));
    this.allRecruitGroups.set(await firstValueFrom(this.getAllRecruitGroups()));
    this.brandRecruitGroups.set(await firstValueFrom(this.getBrandRecruitGroups()));
    if (this.currentUserService.focussedAgency?.id) {
      this.agencyRecruitGroups.set(await firstValueFrom(this.getAgencyRecruitGroups()));
    }
    this.globalRecruitGroups.set(await firstValueFrom(this.getGlobalRecruitGroups()));

    console.log('initRecruitmentGroups() -> myRecruitGroups', this.myRecruitGroups());
    console.log('initRecruitmentGroups() -> allRecruitGroups', this.allRecruitGroups());
    console.log('initRecruitmentGroups() -> brandRecruitGroups', this.brandRecruitGroups());
    console.log('initRecruitmentGroups() -> agencyRecruitGroups', this.agencyRecruitGroups());
    this.notifyCreatorGroupMembershipChanged();
  }

  private clearCachedRecruitmentGroups(): void {
    this.myRecruitGroups.set(undefined);
    this.allRecruitGroups.set(undefined);
    this.brandRecruitGroups.set(undefined);
    this.agencyRecruitGroups.set(undefined);
    this.globalRecruitGroups.set(undefined);
    this.notifyCreatorGroupMembershipChanged();
  }

  getAllRecruitGroups() {
    
    const get_all_recruit_groups_url = `${environment.shout_api.url}/recruit-groups/find-by/all/${this.firebaseService.userId}`;
    return this.http.get<RecruitGroupResponseDto[]>(get_all_recruit_groups_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    }).pipe(tap(res => {
      this.setFilterOptions(res, 'all');
    })) 
  }

  filterRecruitGroups(filters: RecruitGroupFiltersDto) {
    const filter_recruit_groups_url = `${environment.shout_api.url}/recruit-groups/filter`;
    return this.http.post<RecruitGroupResponseDto[]>(filter_recruit_groups_url, filters, {
      headers: {
        'Content-Type': 'application/json',
      }
    }).pipe(tap(res => {
      this.setFilterOptions(res, 'my');
    })) 
  }

  getMyRecruitGroups() {
    const get_my_recruit_groups_url = `${environment.shout_api.url}/recruit-groups/find-by/user/${this.firebaseService.userId}`;
    return this.http.get<RecruitGroupResponseDto[]>(get_my_recruit_groups_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    }).pipe(tap(res => {
      this.setFilterOptions(res, 'my');
    }))
  }

  getRecruitGroupsByTypeAndBusiness(group_type: RecruitGroupTypesEnum, businessAccountId: string): Observable<RecruitGroupsDto> {
    const get_recruit_groups_by_type_and_business_url = `${environment.shout_api.url}/recruit-groups/find/group-type/${group_type}/account/${businessAccountId}`;
    return this.http.get<RecruitGroupsDto>(get_recruit_groups_by_type_and_business_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  getBrandRecruitGroups() {
    const get_brand_recruit_groups_url = `${environment.shout_api.url}/recruit-groups/find-by/business/${this.currentUserService.focussedBrand?.id}`;
    return this.http.get<RecruitGroupResponseDto[]>(get_brand_recruit_groups_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    }).pipe(tap(res => {
      this.setFilterOptions(res, 'brand');
    })) 
  }

  getAgencyRecruitGroups() {
    const get_agency_recruit_groups_url = `${environment.shout_api.url}/recruit-groups/find-by/agency/${this.currentUserService.focussedAgency?.id}`;
    return this.http.get<RecruitGroupResponseDto[]>(get_agency_recruit_groups_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    }).pipe(tap(res => {
      this.setFilterOptions(res, 'agency');
    })) 
  }

  getGlobalRecruitGroups() {
    const get_global_recruit_groups_url = `${environment.shout_api.url}/recruit-groups/find-by/global/${this.firebaseService.userId}`;
    return this.http.get<RecruitGroupResponseDto[]>(get_global_recruit_groups_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    }).pipe(tap(res => {
      this.setFilterOptions(res, 'global');
    })) 
  }

  createRecruitGroup(createRecruitGroupDto: CreateRecruitGroupDto) {
    const create_recruit_group_url = `${environment.shout_api.url}/recruit-groups`;
    return this.http.post<RecruitGroupResponseDto>(create_recruit_group_url, createRecruitGroupDto, {
      headers: {
        'Content-Type': 'application/json',
      }
    })
  }

  updateRecruitGroup(groupId: string, updateRecruitGroupDto: CreateRecruitGroupDto) {
    const update_recruit_group_url = `${environment.shout_api.url}/recruit-groups/${groupId}`;
    return this.http.put<RecruitGroupResponseDto>(update_recruit_group_url, updateRecruitGroupDto, {
      headers: {
        'Content-Type': 'application/json',
      }
    })
  }


  /**
   * Every group the user can see, merged from the five scoped signals and de-duplicated.
   *
   * The same merge was previously copy-pasted in CreatorsMyGroups and AmbalaGroups; the
   * group picker needs it too, so it lives here once.
   */
  readonly visibleGroups = computed<RecruitGroupResponseDto[]>(() => {
    const merged = [
      ...(this.allRecruitGroups() ?? []),
      ...(this.myRecruitGroups() ?? []),
      ...(this.brandRecruitGroups() ?? []),
      ...(this.agencyRecruitGroups() ?? []),
      ...(this.globalRecruitGroups() ?? []),
    ];
    const byId = new Map<string, RecruitGroupResponseDto>();
    for (const group of merged) {
      if (group?.id && !byId.has(group.id)) {
        byId.set(group.id, group);
      }
    }
    return [...byId.values()];
  });

  /**
   * Add creators to a group.
   *
   * The group is the creator list of every brief that references it, so the API also
   * enrols these creators in those briefs — which, for screening, is what starts the screen.
   *
   * `creators` carries hand-entered rows with no creator id; the API matches each on its
   * handle, else creates a creator for it, so they reach the group (and Noodle) too.
   */
  addCreatorsToGroup(
    groupId: string,
    creatorIds: string[],
    creators: AddCreatorByHandleDto[] = [],
  ): Observable<AddCreatorsToGroupResultDto> {
    const url = `${environment.shout_api.url}/recruit-groups/${groupId}/creators`;
    const body = creators.length ? { creatorIds, creators } : { creatorIds };
    return this.http.post<AddCreatorsToGroupResultDto>(url, body, {
      headers: { 'Content-Type': 'application/json' },
    }).pipe(tap(() => this.notifyCreatorGroupMembershipChanged()));
  }

  /** Members of a group, read from the join table rather than the denormalised array. */
  getGroupCreators(groupId: string): Observable<CreatorResponseDto[]> {
    const url = `${environment.shout_api.url}/recruit-groups/${groupId}/creators`;
    return this.http.get<CreatorResponseDto[]>(url);
  }

  /**
   * Remove a creator from a group.
   *
   * Rejects with {@link GROUP_LOCKED_MESSAGE} on a 409: Noodle has no remove-creator
   * endpoint, so once a group has been sent for screening or monitoring it is add-only.
   */
  removeCreatorFromGroup(groupId: string, creatorId: string): Observable<{ removed: string[] }> {
    const url = `${environment.shout_api.url}/recruit-groups/${groupId}/creators/${creatorId}`;
    return this.http.delete<{ removed: string[] }>(url).pipe(
      tap(() => this.notifyCreatorGroupMembershipChanged()),
      catchError((error: HttpErrorResponse) =>
        throwError(() => (error.status === 409 ? new Error(GROUP_LOCKED_MESSAGE) : error)),
      ),
    );
  }

  /** True once the group has a Noodle list, which is when membership becomes add-only. */
  isGroupLocked(group: Pick<RecruitGroupResponseDto, 'sentToNoodle'> | undefined | null): boolean {
    return !!group?.sentToNoodle;
  }

  /** The briefs a group backs, flattened for display. */
  groupUsageBriefs(group: RecruitGroupResponseDto | undefined | null): {
    kind: 'Screening' | 'Monitoring';
    briefId: string;
    briefTitle: string;
  }[] {
    const usage = group?.usage;
    if (!usage) return [];
    return [
      ...usage.screeningBriefs.map((b) => ({ kind: 'Screening' as const, ...b })),
      ...usage.monitoringBriefs.map((b) => ({ kind: 'Monitoring' as const, ...b })),
    ];
  }

  /**
   * @deprecated Toggles membership via the legacy creators route. Prefer
   * {@link addCreatorsToGroup} / {@link removeCreatorFromGroup}, which surface the
   * add-only 409 properly. Retained for AmbalaGroupsComponent for one release.
   */
  setRecruitmentGroup(groupId: string, creatorId: string) {
    const set_recruitment_group_url = `${environment.shout_api.url}/creators/recruitment-groups/set/${groupId}/${creatorId}`;
    return this.http.get<RecruitGroupResponseDto>(set_recruitment_group_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    })
  }

  buildShareUrl(token: string): string {
    const origin = typeof window === 'undefined' ? '' : window.location.origin;
    return `${origin}/groups/share/${token}`;
  }

  createOrGetShareLink(groupId: string): Observable<RecruitGroupShareLinkDto> {
    const share_url = `${environment.shout_api.url}/recruit-groups/${groupId}/share`;
    return this.http.post<RecruitGroupShareLinkDto>(share_url, {}, {
      headers: { 'Content-Type': 'application/json' },
    }).pipe(
      catchError(() => of({ token: groupId, shareUrl: this.buildShareUrl(groupId) })),
    );
  }

  getSharedGroup(token: string): Observable<RecruitGroupShareViewDto> {
    const share_url = `${environment.shout_api.url}/recruit-groups/share/${token}`;
    return this.http.get<RecruitGroupShareViewDto>(share_url, {
      headers: { 'Content-Type': 'application/json' },
    }).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 404) {
          return this.getSharedGroupFallback(token);
        }
        return throwError(() => error);
      }),
    );
  }

  getSharedCreatorVideos(token: string, creatorId: string): Observable<VideoResponseDto[]> {
    const videos_url = `${environment.shout_api.url}/recruit-groups/share/${token}/creator-videos/${creatorId}`;
    return this.http.get<VideoResponseDto[]>(videos_url, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  reviewSharedGroup(token: string, payload: RecruitGroupShareReviewDto): Observable<RecruitGroupShareViewDto> {
    const review_url = `${environment.shout_api.url}/recruit-groups/share/${token}/review`;
    return this.http.post<RecruitGroupShareViewDto>(review_url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  inviteApprovedFromShare(token: string): Observable<RecruitGroupInviteApprovedDto> {
    const invite_url = `${environment.shout_api.url}/recruit-groups/share/${token}/invite-approved`;
    return this.http.post<RecruitGroupInviteApprovedDto>(invite_url, {}, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  emailShareLink(groupId: string, payload: RecruitGroupShareEmailDto): Observable<unknown> {
    const email_url = `${environment.shout_api.url}/recruit-groups/${groupId}/share/email`;
    return this.http.post(email_url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  joinBrandFromShare(token: string): Observable<unknown> {
    const join_url = `${environment.shout_api.url}/recruit-groups/share/${token}/join`;
    return this.http.post(join_url, {}, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  getRecruitGroupById(groupId: string): Observable<RecruitGroupResponseDto> {
    const get_group_url = `${environment.shout_api.url}/recruit-groups/${groupId}`;
    return this.http.get<RecruitGroupResponseDto>(get_group_url, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  private getSharedGroupFallback(token: string): Observable<RecruitGroupShareViewDto> {
    return forkJoin({
      group: this.getRecruitGroupById(token),
      creators: this.http.get<CreatorResponseDto[]>(
        `${environment.shout_api.url}/creators/recruitment-groups/get/${token}`,
        { headers: { 'Content-Type': 'application/json' } },
      ),
    }).pipe(
      map(({ group, creators }) => ({
        group,
        creators,
        shareToken: token,
        canWrite: false,
      })),
    );
  }

  removeGroupAndCreators(groupId: string): Observable<void> {
    const remove_group_and_creators_url = `${environment.shout_api.url}/recruit-groups/remove-group-and-creators`;
    return this.http.post<void>(remove_group_and_creators_url, { groupId }, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  setFilterOptions(groups: RecruitGroupResponseDto[], filterType: 'my' | 'brand' | 'agency' | 'all' | 'global') {
    switch (filterType) {
      case 'all':
        this.allGroupsFilters.set(groups.map(group => ({
          id: group.id,
          name: group.groupName,
          value: group.id,
        })));
        break;
      case 'my':
        this.myGroupsFilters.set(groups.map(group => ({
          id: group.id,
          name: `${group.groupName} (${group.numberOfCreators})`,
          value: group.id,
        })));
        break;
      case 'brand':
        this.brandGroupsFilters.set(groups.map(group => ({
          id: group.id,
          name: `${group.groupName} (${group.numberOfCreators})`,
          value: group.id,
        })));
        break;
      case 'agency':
        this.agencyGroupsFilters.set(groups.map(group => ({
          id: group.id,
          name: `${group.groupName} (${group.numberOfCreators})`,
          value: group.id,
        })));
        break;
      case 'global':
        this.globalGroupsFilters.set(groups.map(group => ({
          id: group.id,
          name: `${group.groupName} (${group.numberOfCreators})`,
          value: group.id,
        })));
        break;
    }
  }
}